import React, { useState, useEffect, useRef } from "react";
import { MdShoppingCart, MdClose } from "react-icons/md";
import { useNavigate, Link } from "react-router-dom";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import { motion, AnimatePresence } from "framer-motion";

const API_BASE = "https://api-vuokraus.spiderlegsproductions.com";

const cartVariants = {
  hidden: { opacity: 0, x: 100 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const panelClass =
  "fixed right-3 top-20 z-50 max-h-[80vh] w-[calc(100vw-1.5rem)] overflow-y-auto rounded-[1.5rem] border border-[#DDE7E5] bg-white/95 p-4 shadow-2xl backdrop-blur-xl dark:border-darkborder dark:bg-[#111114]/95 sm:right-4 sm:w-96 sm:p-5";

const badgeClass =
  "inline-flex rounded-full border border-primary/40 bg-primary/10 px-3 py-1 font-customFont1 text-xs font-extrabold uppercase tracking-[0.12em] !text-[#111827] dark:!text-white";

const mutedTextClass =
  "font-customFont1 text-sm font-semibold leading-relaxed !text-[#4B5563] dark:!text-[#D1D5DB]";

const MiniCart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  // 🔧 Yhtenäinen hinnanluku (toimii sekä laitteille että peräkärrylle)
  const getUnitPrice = (item) =>
    Number(item.pricing?.unitPrice ?? item.price ?? 0);

  const getTotalPrice = (item) =>
    Number(item.pricing?.totalPrice ?? item.totalPrice ?? 0);

  // 📆 Trailerin "vrk" näytetään aina laskutuksen mukaan (total / unit),
  // jotta sulkeet eivät koskaan riitele hinnan kanssa.
  const getTrailerBillableDays = (item) => {
    const unit = getUnitPrice(item);
    const total = getTotalPrice(item);

    if (!unit || !total) return null;

    return Math.round(total / unit);
  };

  const formatPrice = (value) => {
    const number = Number(value) || 0;

    return number.toLocaleString("fi-FI", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getItemImage = (item) => {
    if (item.image?.startsWith("http")) return item.image;
    if (item.image?.startsWith("/assets")) return item.image;

    return `${API_BASE}${item.image || "/assets/placeholder.jpg"}`;
  };

  // 🔄 Päivitä kori localStoragesta
  useEffect(() => {
    const updateCart = () => {
      const rawCart = JSON.parse(localStorage.getItem("rentalCart")) || [];
      setCartItems(rawCart);
    };

    updateCart();

    window.addEventListener("cartUpdated", updateCart);

    return () => {
      window.removeEventListener("cartUpdated", updateCart);
    };
  }, []);

  // ❌ Sulje klikattaessa ulkopuolelle
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 🗑️ Poista tuote korista
  const removeItemFromCart = (productId) => {
    const updatedCart = cartItems.filter((item) => item.id !== productId);

    localStorage.setItem("rentalCart", JSON.stringify(updatedCart));
    setCartItems(updatedCart);
    window.dispatchEvent(new Event("cartUpdated"));
  };

  // 🧮 Yhteissumma – käytä AINA itemin totalPricea
  const totalPrice = cartItems.reduce((sum, item) => {
    return sum + Number(item.pricing?.totalPrice ?? item.totalPrice ?? 0);
  }, 0);

  const itemCount = cartItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 1),
    0
  );

  return (
    <div className="relative" ref={ref}>
      {/* 🛒 Ostoskorin nappi */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="
          relative inline-flex h-11 w-11 items-center justify-center rounded-full
          border border-transparent !text-[#111827]
          transition duration-200
          hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10 hover:!text-primary
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
          focus-visible:ring-offset-2 focus-visible:ring-offset-white
          dark:!text-primary dark:hover:bg-primary/10 dark:focus-visible:ring-offset-[#050505]
        "
        title="Näytä vuokrauskori"
        aria-label="Näytä vuokrauskori"
        aria-expanded={open}
      >
        <MdShoppingCart size={30} />

        {itemCount > 0 && (
          <span
            className="
              absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center
              rounded-full bg-red-600 px-1.5 font-customFont1 text-xs font-extrabold
              text-white shadow-md
            "
          >
            {itemCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="cart"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={cartVariants}
            transition={{ duration: 0.25 }}
            layout
            className={panelClass}
          >
            <div
              aria-hidden="true"
              className="
                pointer-events-none absolute -right-16 -top-16 h-44 w-44
                rounded-full bg-primary/10 blur-3xl
                dark:bg-primary/10
              "
            />

            <div className="relative">
              {/* Otsikko */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className={badgeClass}>Vuokrauskori</span>

                  <h4
                    className="
                      mt-3 font-customFont2 text-4xl font-bold uppercase leading-none
                      !text-[#111827]
                      dark:!text-white
                    "
                  >
                    Vuokrauskori
                  </h4>
                </div>

                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  title="Sulje vuokrauskori"
                  aria-label="Sulje vuokrauskori"
                  className="
                    flex h-10 w-10 shrink-0 items-center justify-center rounded-full
                    border border-[#DDE7E5] bg-[#F8FAFC]
                    !text-[#111827] transition
                    hover:-translate-y-0.5 hover:border-primary hover:bg-primary hover:!text-[#111827]
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                    dark:border-darkborder dark:bg-darksurface dark:!text-white
                    dark:hover:border-primary dark:hover:bg-primary dark:hover:!text-[#111827]
                  "
                >
                  <MdClose size={22} />
                </button>
              </div>

              <div className="my-4 h-px bg-[#DDE7E5] dark:bg-darkborder" />

              {/* Sisältö */}
              {cartItems.length === 0 ? (
                <div
                  className="
                    rounded-3xl border border-[#DDE7E5] bg-[#F8FAFC]/80 p-5
                    text-center shadow-sm
                    dark:border-darkborder dark:bg-darksurface/80
                  "
                >
                  <p className="font-customFont1 text-base font-extrabold !text-[#111827] dark:!text-white">
                    Vuokrauskori on tyhjä.
                  </p>

                  <p className={`${mutedTextClass} mt-2`}>
                    Lisää vuokrattavia tuotteita koriin, niin näet ne täällä.
                  </p>

                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      navigate("/vuokrattavat");
                    }}
                    className="
                      mt-4 rounded-full border border-primary/40 bg-primary/10 px-5 py-2.5
                      font-customFont1 text-sm font-extrabold !text-[#111827]
                      transition
                      hover:-translate-y-0.5 hover:border-primary hover:bg-primary hover:!text-[#111827]
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                      dark:!text-white dark:hover:!text-[#111827]
                    "
                  >
                    Selaa vuokrattavia
                  </button>
                </div>
              ) : (
                <>
                  <ul className="max-h-80 space-y-3 overflow-y-auto pr-1">
                    {cartItems.map((item) => (
                      <li
                        key={`${item.id}-${item.slug || item.name}`}
                        className="
                          relative rounded-3xl border border-[#DDE7E5]
                          bg-[#F8FAFC]/80 p-3 shadow-sm
                          dark:border-darkborder dark:bg-darksurface/80
                        "
                      >
                        <div className="flex items-start gap-3 pr-7">
                          <div
                            className="
                              flex h-16 w-16 shrink-0 items-center justify-center
                              overflow-hidden rounded-2xl border border-[#DDE7E5]
                              bg-white
                              dark:border-darkborder dark:bg-[#111114]
                            "
                          >
                            <img
                              src={getItemImage(item)}
                              alt={item.name}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          </div>

                          <div className="min-w-0 flex-1">
                            <Link
                              to={`/tuote/${item.slug}`}
                              onClick={() => setOpen(false)}
                              className="
                                block break-words font-customFont1 text-sm font-extrabold
                                leading-tight !text-[#111827] transition
                                hover:!text-primary hover:underline
                                dark:!text-white dark:hover:!text-primary
                              "
                            >
                              {item.name}
                            </Link>

                            {/* 💰 HINTA */}
                            <div className="mt-1 font-customFont1 text-sm font-extrabold !text-[#111827] dark:!text-white">
                              {item.flags?.isTrailer ? (
                                <>
                                  {formatPrice(getTotalPrice(item))} €{" "}
                                  {getTrailerBillableDays(item) && (
                                    <span className="block text-xs font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                                      {getTrailerBillableDays(item)} vrk ×{" "}
                                      {formatPrice(getUnitPrice(item))} € / vrk
                                    </span>
                                  )}
                                </>
                              ) : (
                                <>
                                  {item.quantity} ×{" "}
                                  {formatPrice(getUnitPrice(item))} € ={" "}
                                  {formatPrice(getTotalPrice(item))} €
                                </>
                              )}
                            </div>

                            {item.rental && (
                              <div className="mt-1 font-customFont1 text-xs font-semibold italic leading-tight !text-[#4B5563] dark:!text-[#D1D5DB]">
                                {item.flags?.isTrailer ? (
                                  <>
                                    {new Date(
                                      item.rental.startDate
                                    ).toLocaleString("fi-FI", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}{" "}
                                    –{" "}
                                    {new Date(
                                      item.rental.endDate
                                    ).toLocaleString("fi-FI", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                    <br />
                                    Peräkärryn vuokra
                                  </>
                                ) : (
                                  <>
                                    {new Date(
                                      item.rental.startDate
                                    ).toLocaleDateString("fi-FI", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    })}{" "}
                                    –{" "}
                                    {new Date(
                                      item.rental.endDate
                                    ).toLocaleDateString("fi-FI", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    })}
                                    <br />
                                    Laskutus: {item.rental.billableDays} vrk
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeItemFromCart(item.id)}
                          className="
                            absolute right-3 top-3 flex h-7 w-7 items-center justify-center
                            rounded-full border border-transparent
                            !text-[#4B5563] transition
                            hover:border-red-300 hover:bg-red-50 hover:!text-red-600
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400
                            dark:!text-[#D1D5DB] dark:hover:border-red-900/60
                            dark:hover:bg-red-950/40 dark:hover:!text-red-200
                          "
                          title="Poista tuote"
                          aria-label={`Poista ${item.name} vuokrauskorista`}
                        >
                          <MdClose size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>

                  {/* Yhteensä */}
                  <div
                    className="
                      mt-4 rounded-3xl border border-primary/30 bg-primary/10 p-4
                      text-right dark:border-primary/30 dark:bg-primary/5
                    "
                  >
                    <p className="font-customFont1 text-xs font-extrabold uppercase tracking-[0.12em] !text-[#111827] dark:!text-white">
                      Yhteensä
                    </p>

                    <p className="mt-1 font-customFont1 text-3xl font-extrabold !text-[#111827] dark:!text-white">
                      {formatPrice(totalPrice)} €
                    </p>
                  </div>

                  <div className="mt-4">
                    <ResponsiveButton
                      onClick={() => {
                        setOpen(false);
                        navigate("/tee-varaus");
                      }}
                      variant="proceedtocheckout"
                      className="w-full"
                    >
                      Siirry varaamaan
                    </ResponsiveButton>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MiniCart;